import { KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import { useForm } from '@mantine/form';
import { AuthTwoFactorConfirmSetupPayload } from 'api/actions/auth-two-factor-confirm-setup/auth-two-factor-confirm-setup-payload';
import { AuthTwoFactorDisablePayload } from 'api/actions/auth-two-factor-disable/auth-two-factor-disable-payload';
import { AuthTwoFactorStartSetupResponse } from 'api/actions/auth-two-factor-start-setup/auth-two-factor-start-setup-response';
import { Box, Code, CopyButton, Group, Modal, PasswordInput, Stack, TextInput } from '@mantine/core';
import { IconCheck, IconCopy, IconShieldLock, IconTrash } from '@tabler/icons-react';
import FormInputGroup from 'components/forms/FormInputGroup';
import { FormMessage } from 'components/forms/FormProps';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import P1Medium from 'components/typography/P1Medium';
import P1Regular from 'components/typography/P1Regular';
import P2Regular from 'components/typography/P2Regular';
import useLoadingAction from 'hooks/use-loading-action';
import QRCode from 'react-qr-code';
import Toast from 'components/Toast';
import {
  INVALID_PASSWORD_ERROR_CODE,
  INVALID_TWO_FACTOR_CODE_ERROR_CODE,
  TWO_FACTOR_ALREADY_ENABLED_ERROR_CODE,
  TWO_FACTOR_NOT_ENABLED_ERROR_CODE,
  TWO_FACTOR_SETUP_NOT_STARTED_ERROR_CODE,
} from 'utils/constants';
import panic from 'errors/panic';
import Button from 'components/Button';

export interface TwoFactorAuthenticationFormProps {
  enabled: boolean;
  setupPending: boolean;
  requiredByPolicy: boolean;
  latestRecoveryCodes?: string[];
  onClearLatestRecoveryCodes?: () => void;
  onStartSetup?: () => Promise<AuthTwoFactorStartSetupResponse>;
  onConfirmSetup?: (data: AuthTwoFactorConfirmSetupPayload) => Promise<void>;
  onDisable?: (data: AuthTwoFactorDisablePayload) => Promise<void>;
}

/**
 * Form for managing two factor authentication in the user's profile.
 */
export default function TwoFactorAuthenticationForm({
  enabled,
  setupPending,
  requiredByPolicy,
  latestRecoveryCodes,
  onClearLatestRecoveryCodes,
  onStartSetup,
  onConfirmSetup,
  onDisable,
}: TwoFactorAuthenticationFormProps) {
  const [message, setMessage] = useState<FormMessage | undefined>(undefined);
  const [setupData, setSetupData] = useState<AuthTwoFactorStartSetupResponse | null>(null);
  const [showSetupModal, { open: showSetup, close: hideSetup }] = useDisclosure(false);
  const [showDisableForm, { open: showDisable, close: hideDisable }] = useDisclosure(false);

  const setupForm = useForm<AuthTwoFactorConfirmSetupPayload>({
    initialValues: {
      password: '',
      code: '',
    },
    validate: {
      password: createValidator([required]),
      code: createValidator([required]),
    },
  });

  const disableForm = useForm<AuthTwoFactorDisablePayload>({
    initialValues: {
      password: '',
    },
    validate: {
      password: createValidator([required]),
    },
  });

  const closeSetupModal = useCallback(() => {
    setSetupData(null);
    setupForm.reset();
    setupForm.clearErrors();
    onClearLatestRecoveryCodes?.();
    setMessage(undefined);
    hideSetup();
  }, [hideSetup, onClearLatestRecoveryCodes, setupForm]);

  const closeDisableModal = useCallback(() => {
    disableForm.reset();
    disableForm.clearErrors();
    hideDisable();
  }, [disableForm, hideDisable]);

  const [{ loading: startingSetup }, startSetup] = useLoadingAction(async () => {
    setMessage(undefined);
    onClearLatestRecoveryCodes?.();

    try {
      const response = await onStartSetup?.();

      if (response) {
        setSetupData(response);
        hideDisable();
        showSetup();
      }
    } catch (error: any) {
      if (error?.response?.data?.error?.code === TWO_FACTOR_ALREADY_ENABLED_ERROR_CODE) {
        setMessage({ type: 'warning', message: 'Dvojfaktorové overenie už je zapnuté.' });
        return;
      }

      panic(error);
    }
  });

  const [{ loading: confirmingSetup }, confirmSetup] = useLoadingAction(
    async (payload: AuthTwoFactorConfirmSetupPayload) => {
      setupForm.clearErrors();
      setMessage(undefined);

      try {
        await onConfirmSetup?.(payload);
        setupForm.reset();
        setupForm.clearErrors();
        setMessage({
          type: 'success',
          message:
            'Dvojfaktorové overenie bolo zapnuté. Uložte si zobrazené záložné kódy. Po zatvorení okna už k ním nebudete mať prístup.',
        });
      } catch (error: any) {
        const errorCode = error?.response?.data?.error?.code;

        if (errorCode === INVALID_PASSWORD_ERROR_CODE) {
          setupForm.setFieldError('password', 'Zadali ste nesprávne heslo.');
          return;
        }

        if (errorCode === INVALID_TWO_FACTOR_CODE_ERROR_CODE) {
          setupForm.setFieldError('code', 'Zadaný overovací kód je neplatný.');
          return;
        }

        if (errorCode === TWO_FACTOR_SETUP_NOT_STARTED_ERROR_CODE) {
          setMessage({ type: 'warning', message: 'Nastavenie 2FA vypršalo. Vygenerujte nový kľúč.' });
          return;
        }

        if (errorCode === TWO_FACTOR_ALREADY_ENABLED_ERROR_CODE) {
          setMessage({ type: 'warning', message: 'Dvojfaktorové overenie už je zapnuté.' });
          return;
        }

        panic(error);
      }
    }
  );

  const [{ loading: disabling }, disableTwoFactor] = useLoadingAction(async (payload: AuthTwoFactorDisablePayload) => {
    disableForm.clearErrors();
    setMessage(undefined);

    try {
      await onDisable?.(payload);
      closeDisableModal();
      setMessage({ type: 'success', message: 'Dvojfaktorové overenie bolo vypnuté.' });
    } catch (error: any) {
      const errorCode = error?.response?.data?.error?.code;

      if (errorCode === INVALID_PASSWORD_ERROR_CODE) {
        disableForm.setFieldError('password', 'Zadali ste nesprávne heslo.');
        return;
      }

      if (errorCode === TWO_FACTOR_NOT_ENABLED_ERROR_CODE) {
        setMessage({ type: 'warning', message: 'Dvojfaktorové overenie už nie je zapnuté.' });
        return;
      }

      panic(error);
    }
  });

  useEffect(() => {
    if (enabled && !latestRecoveryCodes?.length && (showSetupModal || setupData)) {
      closeSetupModal();
    }
  }, [closeSetupModal, enabled, latestRecoveryCodes, setupData, showSetupModal]);

  const submitSetup = useMemo(() => setupForm.onSubmit(confirmSetup), [setupForm, confirmSetup]);
  const submitDisable = useMemo(() => disableForm.onSubmit(disableTwoFactor), [disableForm, disableTwoFactor]);

  const handleEnterSubmit = useCallback(
    (submitAction: () => void) => (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Enter') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      submitAction();
    },
    []
  );

  const statusText = useMemo(() => {
    if (enabled) {
      return '✅ Dvojfaktorové overenie je aktívne.';
    }

    if (setupPending) {
      return '⏳ Máte rozpracované nastavenie dvojfaktorového overenia. Dokončite ho alebo vygenerujte nový kľúč.';
    }

    return '🔒 Dvojfaktorové overenie zatiaľ nie je aktívne.';
  }, [enabled, setupPending]);

  const statusDescription = useMemo(() => {
    if (enabled) {
      return undefined;
    }

    return 'Po zapnutí dostanete jednorazové záložné kódy pre núdzové prihlásenie.';
  }, [enabled]);

  const modalMessage = showSetupModal || showDisableForm ? message : undefined;
  const inlineMessage = !modalMessage ? message : undefined;

  return (
    <FormInputGroup groupTitle="Dvojfaktorové overenie">
      <Stack spacing={24} maw={720}>
        <Stack spacing={8}>
          <P1Regular>{statusText}</P1Regular>
          {statusDescription && <P2Regular color="gray.6">{statusDescription}</P2Regular>}
        </Stack>

        {requiredByPolicy && (
          <Toast
            type="warning"
            message="Pre vaše konto bude dvojfaktorové overenie v budúcnosti povinné. Odporúčame ho zapnúť už teraz."
            opened
            fullWidth
            withCloseButton={false}
          />
        )}

        {inlineMessage && <Toast {...inlineMessage} opened fullWidth withCloseButton={false} />}

        {!enabled && (
          <Group position="left">
            <Button
              onClick={() => startSetup()}
              loading={startingSetup}
              leftIcon={<IconShieldLock stroke="1.5" width={20} height={20} />}
            >
              {setupPending ? 'Vygenerovať nový 2FA kľúč' : 'Zapnúť dvojfaktorové overenie'}
            </Button>
          </Group>
        )}

        {enabled && (
          <Stack spacing={16}>
            <Group position="left">
              <Button
                type="button"
                variant="danger-secondary"
                leftIcon={<IconTrash stroke="1.5" width={20} height={20} />}
                onClick={() => {
                  setMessage(undefined);
                  showDisable();
                }}
              >
                Vypnúť dvojfaktorové overenie
              </Button>
            </Group>
          </Stack>
        )}

        <Modal
          centered
          opened={showSetupModal && !!setupData}
          onClose={closeSetupModal}
          title="Nastavenie dvojfaktorového overenia"
          size="lg"
          closeOnClickOutside={!confirmingSetup}
          closeOnEscape={!confirmingSetup}
          closeButtonProps={{ loading: confirmingSetup }}
        >
          {setupData && (
            <Stack spacing={24}>
              {modalMessage && <Toast {...modalMessage} opened fullWidth withCloseButton={false} />}

              {!!latestRecoveryCodes?.length ? (
                <Stack spacing={16}>
                  <Box p={16} bg="gray.0" sx={{ borderRadius: 4, border: '1px solid #DEE2E6' }}>
                    <Stack spacing={16}>
                      <P1Medium>Záložné kódy</P1Medium>
                      <Group spacing={12}>
                        {latestRecoveryCodes.map((recoveryCode) => (
                          <Code key={recoveryCode} sx={{ fontSize: 14, padding: '8px 10px' }}>
                            {recoveryCode}
                          </Code>
                        ))}
                      </Group>
                      <Group position="left">
                        <CopyButton value={latestRecoveryCodes.join('\n')} timeout={1500}>
                          {({ copied, copy }) => (
                            <Button type="button" variant="tertiary" leftIcon={<IconCopy size={16} />} onClick={copy}>
                              {copied ? 'Kódy skopírované' : 'Kopírovať všetky kódy'}
                            </Button>
                          )}
                        </CopyButton>
                      </Group>
                    </Stack>
                  </Box>

                  <Group position="right">
                    <Button type="button" onClick={closeSetupModal}>
                      Rozumiem
                    </Button>
                  </Group>
                </Stack>
              ) : (
                <>
                  <Stack spacing={8}>
                    <P1Medium>1. Naskenujte QR kód vo vašej autentifikačnej aplikácii</P1Medium>
                    <Box p={16} bg="white" w="fit-content" sx={{ borderRadius: 4, border: '1px solid #DEE2E6' }}>
                      <QRCode value={setupData.otpauthUrl} size={176} />
                    </Box>
                  </Stack>

                  <Stack spacing={8}>
                    <P1Medium>2. Alebo zadajte tajný kľúč ručne</P1Medium>
                    <Group spacing={12} align="center">
                      <Code sx={{ fontSize: 16, padding: '8px 10px' }}>{setupData.secret}</Code>
                      <CopyButton value={setupData.secret} timeout={1500}>
                        {({ copied, copy }) => (
                          <Button
                            type="button"
                            variant="tertiary"
                            size="xs"
                            leftIcon={<IconCopy size={14} />}
                            onClick={copy}
                          >
                            {copied ? 'Skopírované' : 'Kopírovať'}
                          </Button>
                        )}
                      </CopyButton>
                    </Group>
                  </Stack>

                  <Stack spacing={16}>
                    <P1Medium>3. Potvrďte nastavenie vaším heslom a prvým kódom</P1Medium>
                    <PasswordInput
                      label="Aktuálne heslo"
                      placeholder="Zadajte vaše heslo"
                      size="lg"
                      onKeyDown={handleEnterSubmit(() => submitSetup())}
                      {...setupForm.getInputProps('password')}
                    />
                    <TextInput
                      label="Overovací kód"
                      placeholder="123456"
                      size="lg"
                      onKeyDown={handleEnterSubmit(() => submitSetup())}
                      {...setupForm.getInputProps('code')}
                    />
                    <Group position="left">
                      <Button
                        type="button"
                        loading={confirmingSetup}
                        leftIcon={<IconCheck stroke="1.5" width={20} height={20} />}
                        onClick={() => submitSetup()}
                      >
                        Potvrdiť a zapnúť 2FA
                      </Button>
                      <Button type="button" variant="subtle-gray" onClick={closeSetupModal} disabled={confirmingSetup}>
                        Zrušiť
                      </Button>
                    </Group>
                  </Stack>
                </>
              )}
            </Stack>
          )}
        </Modal>

        <Modal
          centered
          opened={showDisableForm}
          onClose={closeDisableModal}
          title="Vypnúť dvojfaktorové overenie"
          size="md"
          closeOnClickOutside={!disabling}
          closeOnEscape={!disabling}
          closeButtonProps={{ loading: disabling }}
        >
          <Stack spacing={16}>
            {modalMessage && <Toast {...modalMessage} opened fullWidth withCloseButton={false} />}

            <P2Regular color="gray.6">Potvrďte aktuálne heslo a dvojfaktorové overenie bude vypnuté.</P2Regular>
            <PasswordInput
              label="Potvrďte aktuálne heslo"
              placeholder="Zadajte vaše heslo"
              size="lg"
              onKeyDown={handleEnterSubmit(() => submitDisable())}
              {...disableForm.getInputProps('password')}
            />
            <Group position="left">
              <Button
                type="button"
                color="red"
                loading={disabling}
                leftIcon={<IconTrash stroke="1.5" width={20} height={20} />}
                onClick={() => submitDisable()}
              >
                Vypnúť 2FA
              </Button>
              <Button type="button" variant="subtle-gray" onClick={closeDisableModal} disabled={disabling}>
                Zrušiť
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Stack>
    </FormInputGroup>
  );
}
